import React from "react";
import Head from "next/head";
import Link from "next/link";
import { NextPage } from "next";

import GitHubIcon from "src/components/icons/GitHubIcon";
import EngineLogo from "src/components/logos/EngineLogo";
import SignupCallout from "src/components/sales/SignupCallout";
import PlatformExploreBanner from "src/components/sales/PlaformExploreBanner";
import { socialMetadata } from "src/components/layout/Head";

type EngineProps = {
  signup: (mailchimpUser?: any) => void;
};

const Engine: NextPage<EngineProps> = ({ signup }) => (
  <>
    <Head>
        {socialMetadata({
          title: "HASH Engine",
          description: "Open, high-performance, multi-agent simulation engine",
        })}
    </Head>
    <div className="engine-page platform-page engine">
      <div className="mainsite-header with-breadcrumb with-image">
        <div className="container">
          <div className="header-image">
            <EngineLogo />
          </div>
          <div className="header-text">
            <div className="mainsite-breadcrumb">
              <Link href="/platform">
                <a>
                  Platform <span>{">"}</span>
                </a>
              </Link>
            </div>
            <div className="title-with-button">
              <div className="mainsite-title">
                <h1 className="heading-1">
                  <span className="bright-blue">h</span>Engine
                </h1>
                <p className="large-text">Open multi-agent simulation engine</p>
              </div>
              <a href="https://github.com/hashintel/hash" target="_blank">
                <button className="primary-button engine">
                  Star on<span>&nbsp;GitHub</span> <GitHubIcon />
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="platform-content">
        <div className="container">
          <h3 className="heading-4">WHAT IT IS</h3>

          <div className="paras">
            <div className="para">
              <h2 className="para-title">Open sim engine</h2>
              <p>
                <strong>hEngine</strong>
                {" is HASH's next-generation simulation engine. Complementing "}
                <Link href="/platform/core">
                  <a>hCore</a>
                </Link>
                {" and "}
                <Link href="/platform/cloud">
                  <a>hCloud</a>
                </Link>
                {
                  " it provides an open, self-hosted alternative to solutions like "
                }
                <Link href="/vs/anylogic">
                  <a>AnyLogic</a>
                </Link>
                {", FlexSim and "}
                <Link href="/vs/simio">
                  <a>Simio</a>
                </Link>
                .
              </p>
              <p>
                When you build HASH simulations, there’s no risk of platform
                lock-in, and you can switch providers or self-host at any time.
              </p>
              <p>
                The source code for <strong>hEngine</strong> is available{" "}
                <a
                  href="https://github.com/hashintel/labs/tree/main/apps/sim-engine"
                  target="_blank"
                >
                  exclusively on GitHub
                </a>
                .
              </p>
            </div>

            <div className="para">
              <h2 className="para-title">Accessible and extensible</h2>
              <p>
                <strong>hEngine</strong> is written in <em>Rust</em>, and models
                can be written in a variety of additional languages including{" "}
                <em>Python</em>, and JavaScript.
              </p>
              <p>
                We plan to introduce support for <em>TypeScript</em> and{" "}
                <em>R</em> in the coming months, and&nbsp;
                <Link href="/contact">
                  <a>welcome your feedback</a>
                </Link>{" "}
                on where our focus should be subsequently.
              </p>
            </div>

            <div className="para">
              <h2 className="para-title">Best-in-class performance</h2>
              <p>
                HASH was borne of a need for much higher levels of performance
                and scalability than existing commercial simulation engines
                offered.
              </p>
              <p>
                <strong>hEngine</strong> lets users run simulations containing
                thousands of parameters and millions of agents on cheap
                commodity cloud infrastructure.
              </p>
            </div>
          </div>
        </div>

        <div className="form-wrapper">
          <SignupCallout
            Icon={<EngineLogo />}
            interest="ENGINE"
            purple
            signup={signup}
          />
        </div>

        <PlatformExploreBanner exclude="Engine" />
      </div>
    </div>
  </>
);

export default Engine;
